import { NgFor } from '@angular/common';
import {
  Component,
  TemplateRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { PageToolbarComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Store, select } from '@ngrx/store';
import { COUNTRIES } from 'src/assets/data/Countries';

import { ReferenceScheme } from '../../models/reference-scheme.model';
import * as referenceSchemeActions from '../../store/reference-scheme.actions';
import { getReferenceSchemes, getSelectedCountry } from '../../store/reference-scheme.selectors';

@Component({
  selector: 'app-reference-scheme-overview',
  templateUrl: './reference-scheme-overview.component.html',
  styleUrls: ['./reference-scheme-overview.component.scss'],
  imports: [
    PageToolbarComponent,
    MatFormField,
    MatSelect,
    NgFor,
    MatOption,
    TableComponent,
    MatSlideToggle,
    MatButton,
    TranslatePipe,
  ],
})
export class ReferenceSchemeOverviewComponent {
  private readonly localeService = inject(LocaleService);
  private readonly store = inject<Store<RootState>>(Store);

  /* STORE SIGNALS */
  readonly selectedCountry = toSignal(this.store.select(getSelectedCountry));
  private readonly referenceSchemesAll = toSignal(this.store.select(getReferenceSchemes), {
    initialValue: [],
  });
  private readonly isAdvisorSignal = toSignal(
    this.store.pipe(select(AuthorizationSelectors.hasAtLeastAdvisorRole)),
    { initialValue: false },
  );

  /* COMPUTED SIGNALS */
  readonly referenceSchemes = computed(() => {
    const schemes = this.referenceSchemesAll();
    const country = this.selectedCountry();
    return schemes.filter((referenceScheme) => referenceScheme?.country === country);
  });

  readonly isAdvisor = computed(() => this.isAdvisorSignal());

  /* TEMPLATES */
  columnNameTemplate = viewChild<TemplateRef<unknown>>('columnNameTemplate');
  columnActiveTemplate = viewChild<TemplateRef<unknown>>('columnActiveTemplate');

  /* TABLE CONFIG */
  tableConfig = signal<TableConfig>({
    dataSource: new MatTableDataSource([] as ReferenceScheme[]),
    selectColumn: false,
    pagination: false,
    actionColumn: true,
    disableCards: true,
    footer: false,
    scrolling: true,
    columns: [
      {
        id: 'name',
        type: TableColumnTypes.TEMPLATE,
        label: this.translate('table-header.name'),
      },
      {
        id: 'created',
        type: TableColumnTypes.DATE,
        label: this.translate('table-header.created'),
      },
      {
        id: 'updated',
        type: TableColumnTypes.DATE,
        label: this.translate('table-header.updated'),
      },
      {
        id: 'updatedAdvisorName',
        type: TableColumnTypes.STRING,
        label: this.translate('table-header.updatedAdvisorName'),
      },
      {
        id: 'isActive',
        type: TableColumnTypes.TEMPLATE,
        label: this.translate('table-header.isActive'),
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.translate('buttons.edit'),
        callback: (referenceScheme: ReferenceScheme) => {
          if (this.isAdvisor()) {
            this.store.dispatch(
              referenceSchemeActions.editReferenceSchemeNavigation({
                referenceScheme,
              }),
            );
          }
        },
        icon: 'edit',
        disabled: () => !this.isAdvisor(),
      },
      {
        id: 'delete',
        label: this.translate('buttons.delete'),
        callback: (referenceScheme: ReferenceScheme) => {
          if (this.isAdvisor()) {
            this.store.dispatch(
              referenceSchemeActions.deleteReferenceSchemeConfirm({
                referenceScheme,
              }),
            );
          }
        },
        icon: 'delete',
        disabled: () => !this.isAdvisor(),
      },
    ],
  });

  countries: Array<{ id: string; value: string }> = [...COUNTRIES].sort(
    (a: { value: string }, b: { value: string }) => a.value.localeCompare(b.value),
  );

  constructor() {
    /** Applies templates to table columns when ViewChild templates become available */
    effect(() => {
      const nameTpl = this.columnNameTemplate();
      const activeTpl = this.columnActiveTemplate();
      if (!nameTpl || !activeTpl) return;
      untracked(() => {
        this.applyTemplates();
      });
    });

    /** Updates table data when reference schemes change */
    effect(() => {
      const referenceSchemes = this.referenceSchemes();
      const config = this.tableConfig();
      if (referenceSchemes?.length) {
        config.dataSource.data = referenceSchemes;
      } else {
        config.dataSource.data = [];
      }
    });
  }

  private applyTemplates() {
    const newConfig = { ...this.tableConfig() };
    const columnName = newConfig.columns.find((c) => c.id === 'name');
    const columnActive = newConfig.columns.find((c) => c.id === 'isActive');

    if (columnName) {
      columnName.template = this.columnNameTemplate();
    }
    if (columnActive) {
      columnActive.template = this.columnActiveTemplate();
    }
    this.tableConfig.set(newConfig);
  }

  translate(key: string): string {
    return this.localeService.translate(`reference-scheme.overview.${key}`);
  }

  onButtonCreate() {
    if (this.isAdvisor()) {
      this.store.dispatch(referenceSchemeActions.createReferenceSchemeNavigation());
    }
  }

  onToggleActive(referenceScheme: ReferenceScheme) {
    if (this.isAdvisor()) {
      const lReferenceScheme = { ...referenceScheme };
      lReferenceScheme.isActive = !referenceScheme.isActive;
      this.store.dispatch(
        referenceSchemeActions.upsertReferenceSchemeRequest({ referenceScheme: lReferenceScheme }),
      );
    }
  }

  onViewReferenceScheme(referenceScheme: ReferenceScheme) {
    this.store.dispatch(
      referenceSchemeActions.viewReferenceSchemeNavigation({
        referenceScheme: { ...referenceScheme },
      }),
    );
  }

  changeCountrySelection(selection: MatSelectChange) {
    this.store.dispatch(
      referenceSchemeActions.setSelectedCountryRequest({ id: selection.value as string }),
    );
  }
}
